/**
 * EstateMap — estate location with per-building markers (estates-enrichment
 * PR-4; the Rails map tab restored). Buildings without a renderable
 * position are skipped; when no building has one, the estate coordinate
 * renders as a single pin. Renders through `<MapBoundary>` (D13): the map
 * sits below the estate's header and gallery, so it mounts on VIEW — the
 * page's first paint carries a request-free placeholder pinned at the map's
 * centre, and the SDK loads only once the section comes within a viewport.
 */
import { MapBoundary, type MapPlace } from '@/shared/components/map/MapBoundary';
import { AdvancedMarker } from '@/shared/components/map/vis-gl';
import type { BuildingData } from '@/shared/lib/types/estates';
import type { LatLngData } from '@/shared/lib/types/geo';
import { MAP_ZOOM_BUILDING } from '@letsgethome/lib/constants';
import { projectLocalized } from '@letsgethome/lib/i18n';
import { plaintextString, type Locale } from '@letsgethome/types/branded';

type Props = {
  coordinate?: LatLngData;
  buildings: BuildingData[];
  locale: Locale;
  ariaLabel: string;
  /** The street address + district the map falls back to if Google cannot load. */
  place: MapPlace;
};

/**
 * Zoom for an estate's building pins — DERIVED from the SSOT zoom ladder
 * (M/L debt item L6: was a hand literal `16` that silently duplicated the
 * building rung). An estate map centers on individual building markers, so
 * it rides `MAP_ZOOM_BUILDING`, the close-up rung.
 */
const ESTATE_ZOOM = MAP_ZOOM_BUILDING;

export function EstateMap({ coordinate, buildings, locale, ariaLabel, place }: Props) {
  const markers = buildings.filter(
    (b): b is BuildingData & { coordinate: LatLngData } => b.coordinate !== undefined
  );
  const center = markers.length > 0 ? markers[0]!.coordinate : coordinate;
  if (!center) {
    return null;
  }

  const centre = { lat: center.lat, lng: center.lng };

  return (
    <div className="h-[380px] w-full overflow-hidden rounded-xl border border-border">
      <MapBoundary
        load="in-view"
        label={ariaLabel}
        pin={centre}
        place={place}
        map={{
          defaultCenter: centre,
          defaultZoom: ESTATE_ZOOM,
          gestureHandling: 'cooperative',
          disableDefaultUI: false,
        }}
      >
        {markers.length > 0 ? (
          markers.map((b) => (
            <AdvancedMarker
              key={b.id}
              position={{ lat: b.coordinate.lat, lng: b.coordinate.lng }}
              title={plaintextString(projectLocalized(b.fullName, locale))}
            />
          ))
        ) : (
          <AdvancedMarker position={centre} />
        )}
      </MapBoundary>
    </div>
  );
}
